import { Component, inject, signal, ViewContainerRef } from '@angular/core';
import { UsertStore } from '../../../../core/context/userStore';
import { JsonPipe } from '@angular/common';
import { UserService } from '../../services/user-service';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { CommonModule } from '@angular/common';
import { UserHeaderRowItemSearchResult } from '../../../../core/api/mhsIdentityApiClient';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { filter } from 'rxjs/internal/operators/filter';
import { skip, switchMap } from 'rxjs/operators';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { Filters } from './user-filters';
import { Router } from '@angular/router';
import { AuthStore } from '../../../../core/authentication/authStore';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { UserPasswordChange } from '../user-password-change/user-password-change';

@Component({
  selector: 'app-users-view',
  imports: [
    CommonModule,
    NzTableModule,
    NzDividerModule,
    NzFlexModule,
    NzInputModule,
    FormsModule,
    NzDropdownModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
  ],
  templateUrl: './users-view.html',
  styleUrl: './users-view.scss',
})
export class UsersViewComponent {
  messageService = inject(NzModalService);
  userStore = inject(UsertStore);
  userService = inject(UserService);
  router = inject(Router);
  tenantStore = inject(UsertStore);
  searchResult = signal<UserHeaderRowItemSearchResult | null>(null);
  viewContainerRef = inject(ViewContainerRef);
  filters = signal<Filters>({
    query: '',
    isActive: null,
  });

  loading = signal<boolean>(true);
  total = 1;
  pageSize = 10;
  pageIndex = 1;

  constructor() {
    toObservable(this.filters)
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        this.onQueryParamsChange({
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          sort: [],
          filter: [
            { key: 'query', value: value.query },
            { key: 'isActive', value: value.isActive },
          ],
        });
      });

    this.tenantStore.currentTenant$
      .pipe(
        skip(1),
        filter((tenant) => tenant !== null),
        distinctUntilChanged(),
      )
      .subscribe((tenant) => {
        this.onTenantChanged(tenant!.companyName!);
      });
  }
  onTenantChanged(tenant: string): void {
    this.reload();
  }
  reload() {
    this.total = 1;
    this.pageSize = 10;
    this.pageIndex = 1;
    this.filters.update((f) => ({ ...f, query: '' }));
    this.searchResult.set(null);
  }
  queryChange(value: string): void {
    this.filters.update((f) => ({ ...f, query: value }));
  }
  onQueryParamsChange(params: NzTableQueryParams): void {
    this.loading.set(true);
    const { pageSize, pageIndex, sort, filter } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const sortField = currentSort?.key;
    const sortOrder = currentSort?.value || null;
    const phraseFilter = filter.find((item) => item.key === 'query')?.value || '';
    const isActiveFilter = filter.find((item) => item.key === 'isActive')?.value ?? null;
    const isOrderByAsc = sortOrder === 'ascend' ? true : false;
    this.fetchUserData(pageIndex, pageSize, phraseFilter, isActiveFilter, sortField, isOrderByAsc);
  }
  private fetchUserData(
    pageIndex: number,
    pageSize: number,
    phraseFilter: any,
    isActiveFilter: any,
    sortField: string | undefined,
    isOrderByAsc: boolean,
  ) {
    this.userService
      .search(pageIndex, pageSize, phraseFilter, isActiveFilter, sortField, isOrderByAsc)
      .subscribe(
        (result) => {
          this.searchResult.set(result);
          this.total = result.total ?? 0;
          this.loading.set(false);
        },
        (error) => {
          console.error('Error during user search:', error);
          this.loading.set(false);
        },
      );
  }

  detailsClick(userId: number): void {
    this.router.navigate(['/users', userId, 'details']);
  }
  createUserClick(): void {
    this.router.navigate(['/users', 'create']);
  }
  editUserClick(userId: number): void {
    this.router.navigate(['/users', userId, 'edit']);
  }
  deactivateUserClick(userId: number): void {
    this.messageService.confirm({
      nzTitle: 'Czy na pewno chcesz usunąć użytkownika?',
      nzContent: 'Użytkownik zostanie zablokowany i nie będzie mógł się zalogować do systemu.',
      nzOkText: 'Tak, zablokuj',
      nzCancelText: 'Anuluj',
      nzOkDanger: true,
      nzOnOk: () => {
        this.deactivateUser(userId);
      },
    });
  }
  activateUserClick(userId: number): void {
    this.messageService.confirm({
      nzTitle: 'Czy na pewno chcesz aktywować użytkownika?',
      nzContent: 'Użytkownik zostanie odblokowany i będzie mógł się zalogować do systemu.',
      nzOkText: 'Tak, aktywuj',
      nzCancelText: 'Anuluj',
      nzOkDanger: false,
      nzOnOk: () => {
        this.activateUser(userId);
      },
    });
  }
  changePasswordClick(userId: number): void {
    this.createChangePasswordModal(userId);
  }

  deactivateUser(userId: number): void {
    this.loading.set(true);
    this.userService.deactivate(userId).subscribe(() => {
      this.reload();
      this.loading.set(false);
    });
  }
  activateUser(userId: number): void {
    this.loading.set(true);
    this.userService.activate(userId).subscribe(() => {
      this.reload();
      this.loading.set(false);
    });
  }
  createChangePasswordModal(userId: number): void {
    const modal = this.messageService.create<UserPasswordChange, number>({
      nzTitle: 'Zmień hasło użytkownika',
      nzContent: UserPasswordChange,
      nzViewContainerRef: this.viewContainerRef,
      nzData: userId,
      nzFooter: null,
    });
  }
}
